import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { deleteDocument, reprocessDocument, getDocumentWithChunks } from '../../../services/documentService';

/**
 * Document Preview Component
 * Shows document details, chunks, and management actions
 */
const DocumentPreview = ({ document, onClose, onDeleted, onRefresh }) => {
  const [documentDetails, setDocumentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (document?.id) {
      loadDocumentDetails();
    }
  }, [document?.id]);

  const loadDocumentDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const details = await getDocumentWithChunks(document?.id);
      setDocumentDetails(details);
    } catch (err) {
      console.error('Error loading document details:', err);
      setError('Failed to load document details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteDocument(document?.id);
      onDeleted?.();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
      setIsDeleting(false);
    }
  };

  const handleReprocess = async () => {
    try {
      setIsReprocessing(true);
      await reprocessDocument(document?.id);
      setTimeout(() => {
        onRefresh?.();
        setIsReprocessing(false);
      }, 2000);
    } catch (err) {
      console.error('Error reprocessing document:', err);
      setError('Failed to reprocess document');
      setIsReprocessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Document Details</h3>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
      </div>
      {/* Content */}
      <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={24} className="text-primary animate-spin" />
          </div>
        ) : (
          <>
            {/* File Info */}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">File Name</label>
                <p className="text-sm font-medium text-foreground mt-1">{document?.file_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">File Type</label>
                  <p className="text-sm font-medium text-foreground mt-1 uppercase">{document?.file_type}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">File Size</label>
                  <p className="text-sm font-medium text-foreground mt-1">{formatFileSize(document?.file_size)}</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">Upload Date</label>
                <p className="text-sm font-medium text-foreground mt-1">{formatDate(document?.created_at)}</p>
              </div>

              {document?.uploaded_by_profile && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">Uploaded By</label>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {document?.uploaded_by_profile?.first_name} {document?.uploaded_by_profile?.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{document?.uploaded_by_profile?.email}</p>
                </div>
              )}
            </div>

            {/* Processing Status */}
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">Processing Status</label>
                <span className={`text-xs font-medium ${
                  document?.processing_status === 'completed' ? 'text-success' :
                  document?.processing_status === 'processing' ? 'text-warning' :
                  document?.processing_status === 'failed'? 'text-error' : 'text-muted-foreground'
                }`}>
                  {document?.processing_status?.toUpperCase()}
                </span>
              </div>
              {document?.chunk_count > 0 && (
                <p className="text-sm text-foreground">
                  {document?.chunk_count} chunks created
                </p>
              )}
              {document?.error_message && (
                <p className="text-xs text-error mt-2">
                  Error: {document?.error_message}
                </p>
              )}
            </div>

            {/* Chunks Preview */}
            {documentDetails?.chunks && documentDetails?.chunks?.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                  Document Chunks ({documentDetails?.chunks?.length})
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {documentDetails?.chunks?.slice(0, 5)?.map((chunk, index) => (
                    <div key={chunk?.id} className="bg-muted rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-primary">Chunk {index + 1}</span>
                        <span className="text-xs text-muted-foreground">
                          {chunk?.metadata?.word_count || 0} words
                        </span>
                      </div>
                      <p className="text-xs text-foreground line-clamp-3">
                        {chunk?.content}
                      </p>
                    </div>
                  ))}
                  {documentDetails?.chunks?.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      ... and {documentDetails?.chunks?.length - 5} more chunks
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-error" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-4 border-t border-border">
              {document?.processing_status === 'failed' && (
                <Button
                  onClick={handleReprocess}
                  disabled={isReprocessing}
                  className="w-full flex items-center justify-center space-x-2"
                  variant="outline"
                >
                  {isReprocessing ? (
                    <>
                      <Icon name="Loader" size={16} className="animate-spin" />
                      <span>Reprocessing...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="RefreshCw" size={16} />
                      <span>Reprocess Document</span>
                    </>
                  )}
                </Button>
              )}

              {!showDeleteConfirm ? (
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-2 text-error hover:bg-error/10"
                >
                  <Icon name="Trash2" size={16} />
                  <span>Delete Document</span>
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-foreground text-center">
                    Are you sure you want to delete this document?
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setShowDeleteConfirm(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex-1 bg-error hover:bg-error/90 text-white"
                    >
                      {isDeleting ? (
                        <Icon name="Loader" size={16} className="animate-spin" />
                      ) : (
                        'Delete'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentPreview;